import type { Treatment } from '../../types/care';
import {
  LEAFLETS,
  bloodThinners,
  corticosteroidSideEffects,
  injectionResult,
  injectionTimeline,
  reportBasic,
  reportWithPacemaker,
  standardBring,
  standardComplications,
  standardInjectionFacts,
} from './shared';

const sedationNote =
  'De behandeling vindt plaats onder sedatie, omdat het inbrengen van de naald pijnlijk kan zijn. U valt in een lichte slaap en ademt zelf. Hartslag en bloeddruk worden gecontroleerd. U krijgt een infuusnaald waardoor een kortwerkend slaapmiddel wordt gegeven; een gebitsprothese moet u uitdoen.';

const fasting =
  'U moet nuchter zijn. Thuis mag u tot 4 uur voor de behandeling nog een licht ontbijt nuttigen.';

const meningitisRisk =
  'Uiterst zelden komen bij het inbrengen van de naald via de wang mondbacteriën in het hersenvocht, met kans op hersenvliesontsteking. Binnen 6 uur ontstaan dan hoge koorts (> 38,5 °C), hoofdpijn en nekstijfheid; dan moet zo snel mogelijk met antibiotica worden gestart. Daarom krijgt u vooraf antibiotica via het infuus.';

export const nerveProcedures: Treatment[] = [
  {
    slug: 'sweet-procedure',
    name: 'Behandeling volgens Sweet (RF)',
    category: 'Zenuwbehandelingen',
    summary: 'Radiofrequente warmtebehandeling van het ganglion Gasseri bij trigeminusneuralgie.',
    intro: [
      'Trigeminusneuralgie is een vorm van aangezichtspijn met heftige, schietende, elektrische pijnscheuten in het gebied van de aangezichtszenuw (voorhoofd en oog, bovenkaak en neus, of onderkaak). Typisch is dat de pijn wordt opgewekt door een normaal onschuldige prikkel zoals aanraken, eten of praten.',
      'Trigeminusneuralgie wordt eerst met medicatie behandeld (bijvoorbeeld carbamazepine, gabapentine of pregabaline). Is dat onvoldoende, dan kan het ganglion Gasseri worden verdoofd en — bij goed tijdelijk effect — worden behandeld met radiofrequente stroom: de Sweet-procedure.',
    ],
    procedure: [
      sedationNote,
      'Met röntgendoorlichting wordt een elektrode via de wang naar het ganglion Gasseri gebracht. Uw hoofd wordt gefixeerd. Via de naald wordt een klein stroompje gegeven; u geeft aan of u het in het bekende pijngebied voelt tintelen, ter controle van de positie.',
      'Daarna worden een verdovend middel en een irritatieremmer ingespoten en wordt de naaldpunt verwarmd met radiofrequente stroom, waardoor de pijngeleiding wordt onderbroken.',
      'U verblijft daarna in de verkoeverruimte; de injectieplek wordt gekoeld tegen zwelling. Na ongeveer 2 uur mag u naar huis.',
    ],
    facts: { guidance: 'Röntgendoorlichting', home: 'Na ± 2 uur', sedation: true, fasting: true, noDriving: true },
    preparation: [fasting, 'Vooraf krijgt u een infuus met antibiotica.', 'U krijgt vooraf instructies over het gebruik van bloedverdunners.'],
    report: ['Zwangerschap', 'Bloedverdunners', 'Allergieën', 'Pacemaker'],
    bring: standardBring,
    result: [
      'Het resultaat is na enkele dagen tot weken te beoordelen. De verpleegkundig pijnconsulent belt u om de behandeling te evalueren en eventuele vervolgstappen te bespreken.',
    ],
    complications: [
      'Na de behandeling kan een brandende napijn optreden. Soms houden de oorspronkelijke pijnen nog 1 – 3 weken aan; medicatie kunt u in overleg langzaam afbouwen.',
      meningitisRisk,
      'In zeldzame gevallen kan het gevoel van het hoornvlies (oog) verminderen.',
      'Kleine kans op een nabloeding. Drink de eerste 6 uur na de behandeling geen warme dranken.',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Behandeling-volgens-Sweet-RF-VER-FOL-002-OPE-V3.0.pdf`,
  },
  {
    slug: 'sphenopalatinum-blokkade',
    name: 'Blokkade ganglion sphenopalatinum',
    category: 'Zenuwbehandelingen',
    summary: 'Radiofrequente behandeling van een zenuwknoop naast de kaakholte bij clusterhoofdpijn en aangezichtspijn.',
    intro: [
      'Het ganglion sphenopalatinum is een zenuwknoop aan de buitenzijde naast de kaakholte, die een rol kan spelen bij diverse pijnklachten in het gezicht.',
      'Een blokkade wordt onder andere toegepast bij clusterhoofdpijn, atypische aangezichtspijn en oogaandoeningen waarbij medicatie geen effect meer heeft.',
    ],
    procedure: [
      sedationNote,
      'Met röntgendoorlichting wordt een elektrode via de wang naar het ganglion gebracht. Uw hoofd wordt gefixeerd. Als de naald goed staat, wordt een verdovend middel met irritatieremmer ingespoten en wordt de naaldpunt verwarmd met radiofrequente stroom.',
      'U verblijft daarna in de verkoeverruimte; de injectieplek wordt gekoeld tegen zwelling. Na ongeveer een uur mag u naar huis.',
    ],
    facts: { guidance: 'Röntgendoorlichting', home: 'Na ± 1 uur', sedation: true, noDriving: true },
    preparation: [bloodThinners],
    report: ['Gebruik van bloedverdunners', 'Allergieën'],
    bring: standardBring,
    result: [
      'De eerste uren tot een dag kunt u duidelijk minder pijn hebben door de verdoving. Na de radiofrequente behandeling kan een aantal dagen irritatiepijn optreden; dit herstelt vanzelf.',
      'Na enkele dagen belt de pijnspecialist of pijnverpleegkundige u voor een eerste evaluatie, en na ongeveer 8 weken volgt een tweede evaluatie.',
    ],
    complications: [
      'Brandende napijn direct na de behandeling.',
      meningitisRisk,
      'In zeldzame gevallen kan het gevoel van het hoornvlies (oog) verminderen.',
      'Kleine kans op een nabloeding. Drink de eerste 6 uur na de behandeling geen warme dranken.',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Blokkade-van-het-ganglion-sphenopalatinum-VER-FOL-003-OPE-V3.0.pdf`,
  },
  {
    slug: 'foramen-ovale',
    name: 'Injectie foramen ovale',
    category: 'Zenuwbehandelingen',
    summary: 'Infiltratie bij het ganglion Gasseri via het foramen ovale bij aangezichtspijn.',
    intro: [
      'Dit is een behandeling tegen aangezichtspijn. Onder röntgendoorlichting wordt een naald via de wang, door het foramen ovale, bij de kern van de aangezichtszenuw (ganglion Gasseri) geplaatst. Vervolgens worden een verdovend middel (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) geïnfiltreerd.',
    ],
    procedure: [
      sedationNote,
      'Om te voorkomen dat u tijdens de behandeling met uw handen naar uw gezicht reikt of uw hoofd beweegt, worden armen en hoofd gefixeerd.',
      'Na de behandeling wordt de injectieplek gekoeld om zwelling te voorkomen. Na ongeveer 2 uur mag u naar huis.',
    ],
    facts: { guidance: 'Röntgendoorlichting', home: 'Na ± 2 uur', sedation: true, fasting: true, noDriving: true },
    preparation: [fasting, 'Vooraf krijgt u een infuus met antibiotica.', bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: [
      'Na een of twee dagen belt de pijnspecialist u om de behandeling na te bespreken en eventueel medicatieadvies te geven. Na 8 weken kan het effect goed worden beoordeeld.',
    ],
    timeline: injectionTimeline,
    complications: [
      'Na de behandeling kunt u een (niet volledig) doof gevoel hebben in het deel van het gezicht waar de pijn zat. Dit verdwijnt meestal in de maanden daarna deels of helemaal — dat betekent niet dat de pijn terugkomt.',
      meningitisRisk,
      'Kleine kans op een nabloeding.',
      'Tijdelijke hoofdpijn.',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Injectie-foramen-ovale-VER-FOL-009-OPE-V3.0.pdf`,
  },
  {
    slug: 'geniculair-blokkade',
    name: 'Geniculair blokkade (knieblokkade)',
    category: 'Zenuwbehandelingen',
    summary: 'Blokkade van de drie gevoelszenuwen van de knie bij artrose of pijn na een knieprothese.',
    intro: [
      'Bij een geniculair blokkade worden een verdovend medicijn (Chirocaïne/Lidocaïne) en een irritatieremmer (corticosteroïd) rondom de zenuwen van de knie gespoten.',
      'Het gevoel in de knie wordt verzorgd door drie zogenaamde geniculaire zenuwen. Door artrose of na een knieprothese kan chronische kniepijn ontstaan.',
    ],
    procedure: [
      'U ligt op uw rug. Met röntgendoorlichting wordt de positie van de naald bepaald. U krijgt in totaal drie prikken; soms voelt u hierbij een prikkeling.',
      'De arts dient de medicatie toe en verwijdert de naald. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: injectionResult,
    timeline: injectionTimeline,
    complications: standardComplications,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/Geniculair-blokkade-knie-blokkade-VER-FOL-008-OPE-V3.0.pdf`,
  },
  {
    slug: 'prf',
    name: 'PRF-behandeling',
    category: 'Zenuwbehandelingen',
    summary: 'Percutane radiofrequente stroombehandeling om een pijnzenuw minder gevoelig te maken.',
    intro: [
      'Bij een PRF-behandeling wordt een naald vlak bij de zenuw of kleinere zenuwtakjes geplaatst. De naaldpunt wordt gecontroleerd verwarmd met radiofrequente stroom, waardoor de pijngeleiding wordt beïnvloed en de pijn kan afnemen.',
      'Daarnaast worden twee medicijnen toegediend: een snel, kortwerkend verdovend middel (Chirocaïne/Lidocaïne) en een langzaam werkende irritatieremmer (corticosteroïd).',
    ],
    procedure: [
      'Met röntgendoorlichting wordt de plek voor de naald bepaald; soms voelt u hierbij een prikkeling. Indien nodig wordt contrastvloeistof gebruikt om de positie te controleren.',
      'De naaldpunt wordt verwarmd met radiofrequente stroom, waarna de medicatie wordt toegediend en de naald verwijderd. Na ongeveer 30 minuten mag u naar huis.',
    ],
    facts: { ...standardInjectionFacts, guidance: 'Röntgendoorlichting' },
    preparation: [bloodThinners],
    report: reportWithPacemaker,
    bring: standardBring,
    result: [
      'De stroombehandeling kan de eerste dagen (tot 2 weken) irritatiepijn geven. Daardoor duurt het soms enkele dagen of langer voordat u de pijnvermindering ervaart. De zenuw geeft daarna minder pijn door.',
    ],
    timeline: injectionTimeline,
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/PRF-behandeling-VER-FOL-032-OPE-V1.0.pdf`,
  },
  {
    slug: 'racz',
    name: 'RACZ-procedure (adhesiolysis)',
    category: 'Zenuwbehandelingen',
    summary: 'Losmaken van verklevingen en littekenweefsel rond een zenuwwortel bij uitstralende beenpijn.',
    intro: [
      'Een Racz-procedure is een behandeling om uitstralende zenuwpijn te verminderen. De pijn in uw been wordt veroorzaakt door verklevingen en/of littekenweefsel rond een zenuwwortel, bijvoorbeeld na een hernia of een operatie.',
      'De verklevingen worden zoveel mogelijk losgemaakt met een enzym (hyaluronidase), waarna de zenuw wordt geïnfiltreerd met verdovende en ontstekingsremmende medicatie.',
    ],
    procedure: [
      'U ligt op uw buik en krijgt een infuus met eenmalig antibiotica, 1 gram paracetamol en een roesje.',
      'Na verdoving van de huid wordt onder röntgendoorlichting boven het staartbeen een naald ingebracht. Hierdoor wordt een dunne katheter naar de verklevingen geschoven, die met contrastvloeistof zichtbaar worden gemaakt.',
      'Via de katheter krijgt u een verdovend middel, een ontstekingsremmer en het enzym hyaluronidase. De behandeling duurt 30 tot 45 minuten.',
      'Direct na de behandeling start u met neuroflossing: eenvoudige oefeningen die u de eerste keer onder begeleiding doet en daarna thuis voortzet.',
    ],
    facts: { guidance: 'Röntgendoorlichting', duration: '30 – 45 minuten', sedation: true, fasting: true, noDriving: true },
    preparation: [fasting, 'Neem in de ochtend thuis 2 × 500 mg paracetamol in.', bloodThinners],
    report: reportBasic,
    bring: standardBring,
    result: [
      'U kunt na een paar dagen al effect verwachten; het uiteindelijke resultaat is na een paar weken goed te beoordelen. Na ongeveer 8 weken volgt telefonisch contact om de behandeling te evalueren.',
    ],
    complications: [
      ...standardComplications,
      'Hoofdpijn — als het ruggenmergvlies wordt geraakt. Dit gaat meestal vanzelf over; drink veel en blijf zo nodig een paar dagen plat liggen.',
    ],
    sideEffects: [corticosteroidSideEffects],
    pdf: `${LEAFLETS}/RACZ-VER-FOL-021-OPE-V3.0.pdf`,
  },
];
